import { type ColumnDef } from "@tanstack/react-table";

import { Checkbox } from "~/shared/ui/checkbox";

export interface BuildSectionColumnsOptions {
    selecting: boolean;
    selectedIds?: ReadonlySet<string>;
    onToggleSelected?: (id: string) => void;
    allIds?: readonly string[];
    onSelectAll?: (selected: boolean) => void;
    selectAllLabel?: string;
}

/** Resolve a sortable column's display label: a string header, else its id. */
export function columnLabel<TRow>(column: { columnDef: ColumnDef<TRow, unknown>; id: string }): string {
    const header = column.columnDef.header;
    return typeof header === "string" ? header : column.id;
}

/**
 * Prepare caller columns for TanStack v8 rendering:
 *  - inject a no-op `accessorFn` on sortable columns that only define a custom
 *    `cell` (so the header becomes clickable and shows sort arrows — sorting
 *    itself stays manual/client-side via the caller's `onSortingChange`),
 *  - prepend a checkbox `__select` column when selection is active.
 */
export function buildSectionColumns<TRow extends { id: string }>(
    columns: ColumnDef<TRow, unknown>[],
    opts: BuildSectionColumnsOptions,
): ColumnDef<TRow, unknown>[] {
    const { selecting, selectedIds, onToggleSelected, allIds, onSelectAll, selectAllLabel } = opts;
    const patched = columns.map((col) => {
        const hasAccessor = "accessorKey" in col || "accessorFn" in col;
        if (col.enableSorting === true && !hasAccessor) {
            return { ...col, accessorFn: () => null };
        }
        return col;
    });
    if (!selecting || !onToggleSelected) return patched;
    const ids = allIds ?? [];
    const allSelected = ids.length > 0 && ids.every((id) => selectedIds?.has(id));
    const selectColumn: ColumnDef<TRow, unknown> = {
        id: "__select",
        header: () =>
            onSelectAll ? (
                <span onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                        checked={allSelected}
                        onCheckedChange={() => onSelectAll(!allSelected)}
                        aria-label={selectAllLabel ?? "Select all"}
                    />
                </span>
            ) : null,
        enableSorting: false,
        enableHiding: false,
        meta: { section: { cellClassName: "w-11", headClassName: "w-11" } },
        cell: ({ row }) => (
            <span onClick={(e) => e.stopPropagation()}>
                <Checkbox
                    checked={selectedIds?.has(row.original.id) ?? false}
                    onCheckedChange={() => onToggleSelected(row.original.id)}
                    aria-label="Select row"
                />
            </span>
        ),
    };
    return [selectColumn, ...patched];
}
